class StatePopup extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.js-search-input');
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.initialize();
  }

  initialize() {
    this.template = this.getAttribute('data-template');
    this.dialog = this.querySelector('#state-popup');
    this.popupInfo = this.querySelector('.popup__info');
    this.confirmInfo = this.querySelector('.confirm__info');
    this.locationStatus = this.querySelector('#location-status');
    this.popupOpen = false;
    this.confirmButton = this.querySelector('#confirm-selection');
    this.closeButton = this.querySelector('#close-state-popup');
    this.backButton = this.querySelector('#back-btn');
    this.warningButton = this.querySelector('#show-warning-btn');
    this.errorMessage = this.querySelector('#error-message');
    this.zipError = this.querySelector('#zip-error-message');
    this.zipValidationTimer = null;
    this.zipAnnouncementTimer = null;
    this.initializeZipFeedback();
    this.initializeCartWarningFeedback();
    this.cartFeedback = this.querySelector('#location-cart-feedback');
    // Error/recovery feedback is announced by moving focus to this element.
    // Remove live-region semantics to avoid duplicate or repeating VoiceOver speech.
    this.cartFeedback.removeAttribute('aria-live');
    this.cartFeedback.removeAttribute('aria-atomic');
    this.cartStatus = this.querySelector('#location-cart-status');
    this.recoveryInfo = this.querySelector('#location-cart-recovery');
    this.cartChangeKey = 'poc:location-change:v1';
    this.cartBusy = false;
    this.pendingChange = this.readPendingChange();
    this.deliveryLocation = this.querySelector('#delivery-location');
    this.pickupLocation = this.querySelector('#pickup-location');
    this.deliveryMethod = this.querySelector('#delivery__options');
    this.valid_zip = false;
    this.alreadyClicked = false;
    
    // Attach event listeners
    this.addListeners();
    this.applyApprovedCopy();
    this.fetchStateData();
    this.applyNoLocationCommerceState();

    let preferred = null;
    try {
      const storedPreferred = localStorage.getItem('preferredLocation');
      const parsedPreferred = storedPreferred ? JSON.parse(storedPreferred) : null;
      if (parsedPreferred && typeof parsedPreferred === 'object' && parsedPreferred.id) {
        preferred = parsedPreferred;
      }
    } catch (error) {
      preferred = null;
    }

    // Preserve saved-location initialization, but never auto-open the selector
    // when the shopper has not chosen a fulfillment location.
    if (preferred) {
      this.checkLocalStorage();
      this.updateHeaderDisplay(preferred.location, preferred.method);
      this.updateHeaderDisplay(preferred.location, preferred.method, true);
    } else {
      this.updateHeaderDisplay('Shopping location', 'Choose pickup or delivery');
      this.updateHeaderDisplay('Shopping location', 'Choose pickup or delivery', true);
    }
    if (this.pendingChange) {
      this.updateHeaderDisplay('Check shopping location', 'Open to resolve');
      this.updateHeaderDisplay('Check shopping location', 'Open to resolve', true);
    }
  }

  addListeners(){
    this.confirmButton.addEventListener('click', this.handleConfirm.bind(this));
    this.closeButton.addEventListener('click', this.handleClose.bind(this));
    this.backButton.addEventListener('click', this.handleBack.bind(this));
    this.warningButton.addEventListener('click', this.handleWarning.bind(this));
    this.input.addEventListener('input', () => {
      this.cancelZipFeedback();
      // Never let a changed ZIP reuse the previous validated location.
      this.valid_zip = false;
      this.setZipError();
      this.setErrorMessage();
      this.locationStatus.textContent = '';
      this.querySelector('.local__delivery-results').classList.add('hidden');
      this.removeActiveButton(this.deliveryLocation.querySelector('button'));
      // Let character echo and ordinary typing finish before validating.
      this.zipValidationTimer = setTimeout(() => {
        this.zipValidationTimer = null;
        this.handleInput();
      }, 1000);
    });
    this.dialog.addEventListener('keydown', this.handleDialogKeydown.bind(this));
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.consumeEscapeKeyup();
      this.handleClose(event);
    });
    this.dialog.addEventListener('close', () => {
      if (this.popupOpen && !this.dialog.open) this.finishClose();
    });
    this.querySelector('#location-check-again').addEventListener('click', () => this.checkPendingChange());
    this.querySelector('#location-retry-change').addEventListener('click', () => this.retryPendingChange());
    // A persisted uncertain change gates commerce, not ordinary browsing.
    this.commerceGuard = this.guardPendingCommerce.bind(this);
    ['click', 'submit', 'change'].forEach((type) => document.addEventListener(type, this.commerceGuard, true));
    this.deliveyOptions = this.querySelectorAll('.delivery__option');
    this.deliveyOptions.forEach((option) => {
      const radio = option.querySelector('input');
      radio.addEventListener('change', () => this.handleDeliveryOptionClick(option));
      // The tile remains clickable; its native input/label handle their own events.
      option.addEventListener('click', (event) => {
        if (event.target === radio || event.target.closest('label')) return;
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        radio.focus();
      });
    });
  }


  applyApprovedCopy() {
    const title = this.querySelector('#popup-title');
    if (title) title.textContent = 'Choose Pickup or Local Delivery';

    const description = this.querySelector('#popup-description');
    if (description) {
      description.innerHTML = '<p>Select how you\'d like to shop with us. We\'ll use your selection to show availability at the right Paws on Chicon location.</p>';
    }

    const zipContainer = this.querySelector('.zip__container');
    if (zipContainer) {
      let deliverySupport = zipContainer.querySelector('.location-support-text');
      if (!deliverySupport) {
        deliverySupport = document.createElement('p');
        deliverySupport.className = 'location-support-text';
        zipContainer.insertBefore(deliverySupport, zipContainer.firstChild);
      }
      deliverySupport.id = 'location-zip-help';
      deliverySupport.textContent = 'Enter your ZIP code to see if local delivery is available in your area.';

      const zipLabel = zipContainer.querySelector('label[for="zipcode"]');
      if (zipLabel) zipLabel.textContent = 'Delivery ZIP Code';

      const zipInput = zipContainer.querySelector('#zipcode');
      if (zipInput) zipInput.placeholder = 'Enter ZIP Code';
    }

    const pickupResults = this.querySelector('.pickup-results');
    if (pickupResults) {
      let pickupSupport = pickupResults.querySelector('.location-support-text');
      if (!pickupSupport) {
        pickupSupport = document.createElement('p');
        pickupSupport.className = 'location-support-text';
        pickupResults.insertBefore(pickupSupport, pickupResults.firstChild);
      }
      pickupSupport.textContent = 'Choose the Paws on Chicon location where you\'d like to pick up your order.';

      const pickupLabel = pickupResults.querySelector('label[for="pickup-location-button"]');
      if (pickupLabel) pickupLabel.textContent = 'Pickup Location';
    }

    const deliveryLabel = this.querySelector('label[for="delivery-location-button"]');
    if (deliveryLabel) deliveryLabel.textContent = 'Delivery Location';

    if (this.warningButton) {
      this.warningButton.textContent = 'Use This Location';
      // Match the DOM/tab sequence to the existing visual order (action last).
      this.popupInfo.appendChild(this.warningButton.parentElement);
    }

    const warningHeading = this.querySelector('.confirm__info .warning-text');
    if (warningHeading) {
      warningHeading.textContent = 'Changing Your Shopping Location';

      let warningBody = this.querySelector('.confirm__info .warning-body');
      if (!warningBody) {
        warningBody = document.createElement('p');
        warningBody.className = 'warning-body text-center';
        warningHeading.insertAdjacentElement('afterend', warningBody);
      }
      warningBody.id = 'location-warning-description';
      warningBody.textContent = 'Product availability varies by Paws on Chicon location. Your cart is currently tied to your selected store so we can make sure the items you choose are available there. Changing your pickup or delivery location will clear the items currently in your cart. You can then shop using availability at your new location.';
    }

    if (this.backButton) {
      this.backButton.textContent = 'Keep Current Location';
    }

    if (this.confirmButton) {
      this.confirmButton.textContent = 'Change Location & Clear Cart';
    }
  }

  applyNoLocationCommerceState(attempt = 0) {
    let preferred = null;

    try {
      const storedPreferred = localStorage.getItem('preferredLocation');
      const parsedPreferred = storedPreferred ? JSON.parse(storedPreferred) : null;

      if (parsedPreferred && typeof parsedPreferred === 'object' && parsedPreferred.id) {
        preferred = parsedPreferred;
      }
    } catch (error) {
      preferred = null;
    }

    if (preferred) return;

    if (!window.stock && attempt < 20) {
      setTimeout(() => this.applyNoLocationCommerceState(attempt + 1), 100);
      return;
    }

    const productInfo = document.querySelector('.product-info');
    if (!productInfo) return;

    const stockLabel = productInfo.querySelector('.stock__label');
    const stockOutImg = productInfo.querySelector('.stock__label .out-stock');
    const stockImg = productInfo.querySelector('.stock__label .in-stock');

    if (stockLabel && stockLabel.lastElementChild) {
      stockLabel.lastElementChild.textContent = 'Choose pickup or delivery to check store availability';
      stockImg?.classList.add('hidden');
      stockOutImg?.classList.add('hidden');
      stockLabel.classList.remove('in-stock');
    }

    const buyBtns = productInfo.querySelectorAll('.product-info__add-button button');
    buyBtns.forEach((btn) => {
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');

      if (btn.getAttribute('data-type') !== 'promo' && btn.textContent.trim() === 'SOLD OUT') {
        btn.textContent = 'ADD TO CART';
      }
    });
  }

  openPopup(opener = document.activeElement) {
    if (this.popupOpen) return;
    this.returnFocusTo = opener instanceof HTMLElement ? opener : null;
    this.cancelZipFeedback();
    this.resetPopupView();
    this.setErrorMessage();
    this.checkLocalStorage();
    this.setZipError();
    this.cartFeedback.textContent = '';
    this.cartStatus.textContent = '';
    this.pendingChange = this.readPendingChange();
    if (this.pendingChange) this.showCartRecovery();
    this.classList.remove('hide');
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.popupOpen = true;
    // Native modality makes the background inert, including other open drawers.
    this.dialog.showModal();
    const title = this.querySelector(this.pendingChange ? '#location-recovery-title' : '#popup-title');
    (title || this.closeButton).focus({ preventScroll: true });
  }

  resetPopupView() {
    this.clearCartWarningAnnouncement();
    this.recoveryInfo.classList.add('hidden');
    this.backButton.textContent = 'Keep Current Location';
    this.popupInfo.classList.remove('hidden');
    this.confirmInfo.classList.add('hidden');
    this.dialog.setAttribute('aria-labelledby', 'popup-title');
    this.dialog.setAttribute('aria-describedby', 'popup-description');
  }

  closePopup() {
    if (this.cartBusy) return;
    if (this.dialog.open) this.dialog.close();
    if (this.popupOpen) this.finishClose();
    else this.classList.add('hide');
  }

  finishClose() {
    this.popupOpen = false;
    this.cancelZipFeedback();
    this.setZipError();
    this.classList.add('hide');
    this.resetPopupView();
    document.documentElement.style.overflow = this.previousOverflow || '';
    this.querySelectorAll('custom-dropdown-select').forEach((select) => {
      select.hideListbox({ restoreFocus: false });
    });
    const canFocus = (element) => element?.isConnected && !element.disabled &&
      !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0 &&
      getComputedStyle(element).visibility !== 'hidden';
    const target = [this.returnFocusTo,
      document.getElementById('change-selection'),
      document.getElementById('mbl-change-selection')].find(canFocus);
    target?.focus({ preventScroll: true });
    this.returnFocusTo = null;
    this.recoveryDesired = null;
  }

  getTabStops() {
    const elements = Array.from(this.dialog.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]'
    )).filter((element) => element.tabIndex >= 0 && !element.disabled &&
      !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== 'hidden');
    // A native radio group occupies a single Tab stop.
    return elements.filter((element) => {
      if (!element.matches('input[type="radio"]') || !element.name) return true;
      const group = elements.filter((item) => item.matches('input[type="radio"]') &&
        item.name === element.name);
      return element === (group.find((item) => item.checked) || group[0]);
    });
  }

  handleDialogKeydown(event) {
    if (event.defaultPrevented || !this.popupOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.consumeEscapeKeyup();
      this.handleClose(event);
    } else if (event.key === 'Tab') {
      if (this.cartBusy) {
        event.preventDefault();
        this.cartStatus.focus();
        return;
      }
      const stops = this.getTabStops();
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      if (!first) {
        event.preventDefault();
        this.closeButton.focus();
      } else if (event.shiftKey && (active === first || !stops.includes(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  consumeEscapeKeyup() {
    // Focus may already be back in Quick Add when Escape is released. Do not
    // let that same keystroke also dismiss the underlying drawer.
    if (this.escapeKeyupCleanup) this.escapeKeyupCleanup();
    let timer;
    const cleanup = () => {
      window.removeEventListener('keyup', handler, true);
      clearTimeout(timer);
      this.escapeKeyupCleanup = null;
    };
    const handler = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      cleanup();
    };
    this.escapeKeyupCleanup = cleanup;
    window.addEventListener('keyup', handler, true);
    timer = setTimeout(cleanup, 1000);
  }

  checkLocalStorage() {
    let preferred = null;

    try {
      const storedPreferred = localStorage.getItem('preferredLocation');
      const parsedPreferred = storedPreferred ? JSON.parse(storedPreferred) : null;

      if (parsedPreferred && typeof parsedPreferred === 'object' && parsedPreferred.id) {
        preferred = parsedPreferred;
      }
    } catch (error) {
      preferred = null;
    }

    if (preferred) {
      this.deliveyOptions.forEach(option => {
        const input = option.querySelector('input');
        input.checked = false;
        if(preferred.method === input.value){
          input.checked = true;
          this.deliveryMethod.setAttribute('data-selected-value', input.value);
        }
      });
      // set active list for location and method
      const methods = ['Local Delivery', 'In-Store Pickup']
      if(!methods.includes(preferred.method)){
        return;
      }
      if(preferred.method === 'Local Delivery'){
        this.valid_zip = true;
        this.querySelector('#zipcode').value = preferred?.zip;
        const locations = this.getLocations(preferred?.zip);
        this.deliveryLocation.populateOptions(locations);
        this.setActiveList(this.deliveryLocation,preferred.location, preferred.id);
        this.deliveryLocation.querySelector('button').disabled = true;
        this.deliveryClasses();
      } else {
        const locations = this.getLocations();
        this.pickupLocation.populateOptions(locations);
        this.setActiveList(this.pickupLocation,preferred.location, preferred.id);
        this.pickupClasses();
      }
      this.filterProductsByTag(preferred.id);
    } else {
      if(!this.alreadyClicked) {
        for(const option of Array.from(this.deliveyOptions)){
          const input = option.querySelector('input');
          input.checked = true;
          this.deliveryMethod.setAttribute('data-selected-value', input.value);
          break;
        };
      }
      if (this.deliveryMethod.getAttribute('data-selected-value') === 'In-Store Pickup') {
        this.pickupClasses();
      } else {
        this.deliveryClasses();
        this.querySelector('.local__delivery-results').classList.add('hidden');
      }
    }
  }

  pickupClasses(){
    this.querySelector('.zip__container').classList.add('hidden');
    this.querySelector('.pickup-results').classList.remove('hidden');
    this.querySelector('.local__delivery-results').classList.add('hidden');
  }

  deliveryClasses(){
    this.querySelector('.zip__container').classList.remove('hidden');
    this.querySelector('.local__delivery-results').classList.remove('hidden');
    this.querySelector('.pickup-results').classList.add('hidden');
  }

  handleWarning(evt) {
    evt.preventDefault();
    return this.performLocationChange(false);
  }

  handleBack() {
    if (this.cartBusy) return;
    if (this.readPendingChange() && this.recoveryDesired) {
      this.showCartRecovery();
      this.querySelector('#location-recovery-title').focus();
      return;
    }
    this.closePopup();
  }

  initializeZipFeedback() {
    // Keep the visual error as the field's description, but use a separate,
    // persistent live region for speech. Clearing speech never flickers the UI.
    this.zipError.removeAttribute('aria-live');
    this.zipError.removeAttribute('aria-atomic');
    this.zipAnnouncement = this.querySelector('#location-zip-announcement');
    if (!this.zipAnnouncement) {
      this.zipAnnouncement = document.createElement('div');
      this.zipAnnouncement.id = 'location-zip-announcement';
      this.zipAnnouncement.className = 'visually-hidden';
      this.zipAnnouncement.setAttribute('role', 'alert');
      this.zipAnnouncement.setAttribute('aria-atomic', 'true');
      this.dialog.querySelector('.popup-content').appendChild(this.zipAnnouncement);
    }
    this.clearZipAnnouncement();
  }

  clearZipAnnouncement() {
    clearTimeout(this.zipAnnouncementTimer);
    this.zipAnnouncementTimer = null;
    this.zipAnnouncementKey = null;
    if (this.zipAnnouncement) this.zipAnnouncement.textContent = '';
  }

  cancelZipFeedback() {
    clearTimeout(this.zipValidationTimer);
    this.zipValidationTimer = null;
    this.clearZipAnnouncement();
  }

  setZipError(message = '', invalid = true) {
    if (this.zipError.textContent !== message) this.zipError.textContent = message;
    if (message && invalid) this.input.setAttribute('aria-invalid', 'true');
    else this.input.removeAttribute('aria-invalid');
    if (!message) {
      this.clearZipAnnouncement();
      return;
    }
    const zip = this.input.value;
    const key = JSON.stringify([zip, message]);
    if (key === this.zipAnnouncementKey) return;
    this.clearZipAnnouncement();
    if (!this.popupOpen || this.cartBusy ||
        this.deliveryMethod.getAttribute('data-selected-value') !== 'Local Delivery' ||
        this.popupInfo.classList.contains('hidden')) return;
    this.zipAnnouncementKey = key;
    // A distinct empty-to-text update also supports a repeated invalid attempt.
    // These timers create DOM updates, not a guarantee of assistive-tech speech.
    this.zipAnnouncementTimer = setTimeout(() => {
      this.zipAnnouncementTimer = null;
      if (!this.isConnected || !this.popupOpen || this.cartBusy ||
          this.input.value !== zip || this.zipError.textContent !== message ||
          this.deliveryMethod.getAttribute('data-selected-value') !== 'Local Delivery' ||
          this.popupInfo.classList.contains('hidden')) return;
      this.announceZipError(`Delivery ZIP Code: ${message}`);
    }, 150);
  }

  announceZipError(message) {
    // A supported direct notification makes a fresh request even when an
    // earlier validation attempt used the same error text. Keep the visible
    // field description and the established live-region fallback unchanged.
    const policy = this.ownerDocument.permissionsPolicy || this.ownerDocument.featurePolicy;
    if (typeof this.dialog.ariaNotify === 'function') {
      try {
        if (!policy || typeof policy.allowsFeature !== 'function' ||
            policy.allowsFeature('aria-notify')) {
          this.dialog.ariaNotify(message, { priority: 'high' });
          return; // Do not also update the alert and request duplicate speech.
        }
      } catch (error) {
        // Unavailable or denied native notifications fall back to the alert.
      }
    }
    this.zipAnnouncement.textContent = message;
  }

  handleInput(force = false) {
    if (!this.popupOpen || this.deliveryMethod.getAttribute('data-selected-value') !== 'Local Delivery' || this.cartBusy) return;
    this.valid_zip = false;
    this.locationStatus.textContent = '';
    this.removeActiveButton(this.deliveryLocation.querySelector('button'));
    this.deliveryLocation.querySelector('.custom-select__listbox').innerHTML = '';
    this.querySelector('.local__delivery-results').classList.add('hidden');
    // A blank field is an instruction, not an error, until the shopper tries
    // to continue. Explicit validation still blocks an empty or incomplete ZIP.
    if (this.input.value === '' && !force) {
      this.setZipError();
      return;
    }
    if (!/^[0-9]{5}$/.test(this.input.value)) {
      this.setZipError('Enter a valid 5-digit ZIP code.');
      return;
    }
    const value = Number(this.input.value);
    const unavailable = "Local delivery isn't available for this ZIP code. Choose In-Store Pickup to shop at any Paws on Chicon location.";
    // Preserve the exported ZIP range gate and app-provided ZIP/store mappings.
    if (value < 73301 || value > 88595) {
      this.setZipError(unavailable);
      return;
    }
    let locations;
    try {
      const data = JSON.parse(sessionStorage.getItem('locations'));
      if (!Array.isArray(data?.processedLocations)) throw new Error('Location data unavailable');
      locations = this.getLocations(this.input.value);
    } catch (error) {
      this.setZipError('We could not check delivery availability right now. Please wait a moment and try again.', false);
      // Retry a failed/missing app response; never describe it as an unsupported ZIP.
      this.fetchStateData();
      return;
    }
    if (!locations.length) {
      this.setZipError(unavailable);
      return;
    }
    this.deliveryLocation.populateOptions(locations);
    const button = this.deliveryLocation.querySelector('button');
    if (!button.getAttribute('data-id')) {
      this.setZipError('We could not check delivery availability right now. Please try again.', false);
      return;
    }
    this.setZipError();
    this.setErrorMessage();
    this.valid_zip = true;
    button.disabled = true;
    this.querySelector('.local__delivery-results').classList.remove('hidden');
    this.locationStatus.textContent = `Local delivery is available from ${button.getAttribute('data-value')}.`;
  }

  getLocations (zip = null) {
    const data = JSON.parse(sessionStorage.getItem('locations')) || null;
    if (!data || !data?.processedLocations) {
      return [];
    }
    // console.log('get locations zip', zip);
    if(zip){
      return data.processedLocations.filter(loc => loc?.zipCodes?.includes(zip));
    } else {
      return data.processedLocations;
    }
  }

  setErrorMessage(errorMessage, hidden = false) {
    this.errorMessage.textContent = !hidden && errorMessage ? errorMessage : '';
    // General selection errors are not ZIP-format errors.
  }

  removeActiveButton(element, text = null){
    element.removeAttribute('data-value');
    element.removeAttribute('data-id');
    element.firstElementChild.textContent = text != null ? text : '';
  }

  handleDeliveryOptionClick(option){
    this.cancelZipFeedback();
    this.deliveyOptions.forEach(option => option.querySelector('input').checked = false);
    const input = option.querySelector('input');
    input.checked = true;
    this.deliveryMethod.setAttribute('data-selected-value', input.value);
    this.locationStatus.textContent = '';
    this.deliveryLocation.hideListbox({ restoreFocus: false });
    this.pickupLocation.hideListbox({ restoreFocus: false });
    if(input.value === 'In-Store Pickup'){
      this.valid_zip = false;
      this.pickupClasses();
      this.pickupLocation.clearSelection();
    } else {
      this.input.value = '';
      this.deliveryClasses();
      this.removeActiveButton(this.deliveryLocation.querySelector('button'));
      this.deliveryLocation.querySelector('.custom-select__listbox').innerHTML = '';
      this.querySelector('.local__delivery-results').classList.add('hidden');
    }
    this.setErrorMessage();
    this.setZipError();
    this.alreadyClicked = true;
  }

  fetchStateData() {
    if (this.locationRequest) return this.locationRequest;
    try {
      if (Array.isArray(JSON.parse(sessionStorage.getItem('locations'))?.processedLocations)) return;
    } catch (error) { /* A corrupt cache is refreshed, not treated as ZIP rejection. */ }
    this.locationRequest = this.requestJSON('/apps/stock_info/location')
      .then((data) => {
        if (!Array.isArray(data?.processedLocations)) throw new Error('Invalid location data');
        const selectedPickupId = this.pickupLocation.querySelector('button').getAttribute('data-id');
        const zipcodes = data.processedLocations.map((location) => location.zipCodes || '').join(', ');
        sessionStorage.setItem('zip_codes', JSON.stringify(zipcodes));
        sessionStorage.setItem('locations', JSON.stringify(data));
        this.pickupLocation.populateOptions(data.processedLocations);
        if (selectedPickupId) {
          const selected = data.processedLocations.find((location) => location.locationId.split('/').pop() === selectedPickupId);
          if (selected) this.setActiveList(this.pickupLocation, selected.locationName, selectedPickupId);
        }
        if (this.popupOpen && !this.cartBusy && this.input.value && !this.zipValidationTimer &&
            this.deliveryMethod.getAttribute('data-selected-value') === 'Local Delivery') this.handleInput();
      })
      .catch((error) => {
        console.warn('Location data could not be loaded.', error);
        if (this.popupOpen && !this.cartBusy) {
          if (this.deliveryMethod.getAttribute('data-selected-value') === 'Local Delivery') {
            // Do not report a service failure against an untouched/partial ZIP
            // or interrupt a newer edit whose validation is still scheduled.
            if (/^[0-9]{5}$/.test(this.input.value) && !this.zipValidationTimer) {
              this.setZipError('We could not check delivery availability right now. Please try again.', false);
            }
          } else {
            this.setErrorMessage('We could not load pickup locations right now. Please try again.');
          }
        }
      })
      .finally(() => { this.locationRequest = null; });
    return this.locationRequest;
  }

  setActiveList(element, value, dataId = null){
    const button = element.querySelector('button');
    const list = element.querySelector('ul');
    const options = element.querySelectorAll('li');
    element.selectedOption = Array.from(options).find((option) =>
      dataId ? option.getAttribute('data-id') === dataId : option.getAttribute('data-value') === value
    ) || null;
    button.setAttribute('data-value', value);
    button.firstElementChild.textContent = value;
    if(dataId) button.setAttribute('data-id', dataId);
    options.forEach(option => {
      if(option.firstElementChild.textContent === value){
        option.setAttribute('aria-selected', 'true');
        option.classList.add('is-focused');
        list.setAttribute('aria-activedescendant', option.id);
      } else {
        option.setAttribute('aria-selected', 'false');
        option.classList.remove('is-focused');
      }
    });
  }

  async refreshCurrentVariantStock() {
    if (this.template !== 'product') return;

    const productInfo = document.querySelector('.product-info');
    const variantId = productInfo?.querySelector('[name="id"]')?.value;
    if (!variantId) return;

    try {
      const response = await fetch('/apps/stock_info/inventory_levels', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: variantId })
      });

      if (!response.ok) {
        throw new Error(`Inventory request failed: ${response.status}`);
      }

      const stockData = await response.json();
      window.stock = stockData;

      const stockInline = document.querySelector('stock-inline');
      const policy = productInfo.getAttribute('data-inventory-policy');

      if (stockInline) {
        this.updateCountDisplay();
      }

      if (policy !== 'continue') {
        this.toggleButtonDisable();
      }
    } catch (error) {
      console.error('Error refreshing stock after location change:', error);

      const policy = productInfo?.getAttribute('data-inventory-policy');
      if (policy !== 'continue') {
        this.diableButton(true);
      }
    }
  }

  handleConfirm(evt) {
    evt.preventDefault();
    return this.performLocationChange(true);
  }

  handleClose(evt) {
    if (this.cartBusy) return;
    this.setErrorMessage();
    this.setZipError();
    this.closePopup();
  }
  
  filterProductsByTag(location) {
    const products = document.querySelectorAll('.product-card');
    if(products && products.length > 0) {
      products.forEach((product) => {
        const policy = product.getAttribute('data-inventory-policy') === 'continue';
        if(!policy){
          const locationTags = product?.getAttribute('data-locations')?.split(',');
          const unavailableLabel = product.querySelector('.product-label-location-out');
          const button = product.querySelector('button');
          if (!locationTags?.includes(location)) {
            product.classList.add('location-sold-out');
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            if(unavailableLabel) unavailableLabel.classList.remove('hidden');
          } else {
            product.classList.remove('location-sold-out');
            button.disabled = false;
            button.setAttribute('aria-disabled', 'false');
            if(unavailableLabel) unavailableLabel.classList.add('hidden');
          }
        }
      });
    }
  }
  // update header for location
  updateHeaderDisplay(selectedState = 'Shopping location', selectedDM = 'Choose pickup or delivery', mobile=false) {
    const currentLElem = document.getElementById(`${mobile ? 'mbl-' : ''}current-state`);
    const currentDMElem = document.getElementById(`${mobile ? 'mbl-' : ''}current-delivery-method`);

    if (currentLElem) currentLElem.textContent = `${selectedState}`;
    if (currentDMElem) currentDMElem.textContent = `${selectedDM}`;
  }

  // update stock availability for location changed
  updateCountDisplay() {
    let preferred = null;

    try {
      const storedPreferred = localStorage.getItem('preferredLocation');
      const parsedPreferred = storedPreferred ? JSON.parse(storedPreferred) : null;

      if (parsedPreferred && typeof parsedPreferred === 'object' && parsedPreferred.id) {
        preferred = parsedPreferred;
      }
    } catch (error) {
      preferred = null;
    }

    const counts = document.querySelectorAll('.stock__count--inline');
    const p = document.querySelector(".stock__availability");

    if (!preferred) {
      counts.forEach((count) => count.classList.add('hidden'));
      if (p) {
        let neutral = p.querySelector('.stock__count--inline.stock__location-required');
        if (!neutral) {
          p.insertAdjacentHTML(
            'beforeend',
            '<span class="stock__count--inline stock__location-required">Choose pickup or delivery to check store availability</span>'
          );
        } else {
          neutral.classList.remove('hidden');
        }
      }
      return;
    }

    const neutral = p?.querySelector('.stock__count--inline.stock__location-required');
    if (neutral) neutral.classList.add('hidden');

    const unavailable = document.querySelector(`.stock__count--inline.location\\:${preferred.id}`);
    counts.forEach((count) => {
      if(count.classList.contains(`location:${preferred.id}`)) {
        count.classList.remove('hidden');
      } else {
        count.classList.add('hidden');
      }
    })
    if(!unavailable && p){
      p.insertAdjacentHTML('beforeend', `<span class="stock__count--inline location:${preferred.id} stock__out">Unavailable</span>`)
    }
  }

  toggleButtonDisable(){
    const quantityInput = document.querySelector('.product-info .qty-input__input');
    const plusButton = document.querySelector('.product-info .qty-input__btn[name="plus"]');
    if (quantityInput) quantityInput.value = 1;
    if (plusButton) plusButton.disabled = false;

    const stockOutImg = document.querySelector('.stock__label .out-stock');
    const stockImg = document.querySelector('.stock__label .in-stock');
    const hasStockLabelImgs = stockOutImg && stockImg;

    let preferred = null;

    try {
      const storedPreferred = localStorage.getItem('preferredLocation');
      const parsedPreferred = storedPreferred ? JSON.parse(storedPreferred) : null;

      if (parsedPreferred && typeof parsedPreferred === 'object' && parsedPreferred.id) {
        preferred = parsedPreferred;
      }
    } catch (error) {
      preferred = null;
    }

    if (!preferred) {
      const stockLabel = document.querySelector('.stock__label');
      if (stockLabel) {
        stockLabel.lastElementChild.textContent = 'Choose pickup or delivery to check store availability';
        stockImg?.classList.add('hidden');
        stockOutImg?.classList.add('hidden');
        stockLabel.classList.remove('in-stock');
      }
      return;
    }

    // console.log(window.stock);
    const stockFound = window?.stock?.find(item => {
      if (!item?.locationId) return false;
      const id = item.locationId.split('/').pop();
      return id === preferred.id;
    });
    if(stockFound) {
      const stockCount = stockFound.stock;
      // console.log(stockCount);
      if(stockCount >= 1){
        this.diableButton(false, 'ADD TO CART');
        if (hasStockLabelImgs) this.showLabel(stockImg, stockOutImg, true);
      } else {
        this.diableButton(true, 'SOLD OUT');
        if (hasStockLabelImgs) this.showLabel(stockImg, stockOutImg, false);
      }
    } else {
      this.diableButton(true, 'SOLD OUT');
      if (hasStockLabelImgs) this.showLabel(stockImg, stockOutImg, false);
    }
  }

  showLabel(stockImg, stockOutImg, stock = false){
    const stockLabel = document.querySelector('.stock__label');
    if (!stockLabel) return;
    stockLabel.lastElementChild.textContent = stock ? "In stock": "Out of stock";
    if(stock){
      stockImg?.classList.remove('hidden');
      stockOutImg?.classList.add('hidden');
      stockLabel.classList.add('in-stock');
    } else {
      stockImg?.classList.add('hidden');
      stockOutImg?.classList.remove('hidden');
      stockLabel.classList.remove('in-stock');
    }
  }

  diableButton(disabled = true, text = null) {
    const dynamicBtn = document.querySelector(
      '.product-info__dynamic-checkout-btn shopify-accelerated-checkout'
    );
    const buyBtns = document.querySelectorAll('.product-info__add-button button');
    buyBtns.forEach(btn => {
      btn.disabled = disabled ? true : false;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      const promoBtn = btn.getAttribute('data-type') === 'promo';
      const btnText = btn.textContent;
      if(text) btn.textContent = promoBtn ? btnText : text;
    })
    if (dynamicBtn) dynamicBtn.disabled = disabled ? true : false;
  }

  // This journal is only for an interrupted location change, not normal browsing.
  readPendingChange() {
    try {
      const raw = localStorage.getItem(this.cartChangeKey);
      if (!raw) return null;
      let data;
      try { data = JSON.parse(raw); } catch (error) { return { version: 0 }; }
      return data?.version === 1 && data.desired?.id ? data : { version: 0 };
    } catch (error) {
      return this.pendingChange || null;
    }
  }

  readPreferred() {
    try {
      const value = JSON.parse(localStorage.getItem('preferredLocation'));
      return value?.id && value.method && value.location ? value : null;
    } catch (error) { return null; }
  }

  locationAttributes(preferred) {
    return {
      store_id: preferred?.id || '', store: preferred?.location || '',
      shipping_type: preferred?.method || '', zip: preferred?.zip || (preferred ? 'nil' : '')
    };
  }

  attributesMatch(cart, attributes) {
    return Object.keys(attributes).every((key) => String(cart.attributes?.[key] || '') === String(attributes[key] || ''));
  }

  readSelection() {
    this.setErrorMessage();
    const method = this.deliveryMethod.getAttribute('data-selected-value');
    if (method === 'Local Delivery') {
      this.cancelZipFeedback();
      this.handleInput(true);
      if (!this.valid_zip) { this.input.focus(); return null; }
    }
    const button = (method === 'Local Delivery' ? this.deliveryLocation : this.pickupLocation).querySelector('button');
    const id = button.getAttribute('data-id');
    const location = button.getAttribute('data-value');
    if (!['Local Delivery', 'In-Store Pickup'].includes(method) || !id || !location) {
      this.setErrorMessage('Choose a pickup location before continuing.');
      this.errorMessage.focus();
      this.fetchStateData();
      return null;
    }
    const result = { id, location, method };
    if (method === 'Local Delivery') result.zip = this.input.value;
    return result;
  }

  cartURL(action = '') {
    const root = window.Shopify?.routes?.root || '/';
    if (!action) return `${root}cart.js`;
    const route = action === 'clear' ? theme.routes.cartClear : theme.routes.cartUpdate;
    return /\.js$/.test(route) ? route : `${route}.js`;
  }

  async requestJSON(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.requestTimeoutMs || 12000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', ...options,
        headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal
      });
      if (!response.ok) {
        const error = new Error(`Request failed (${response.status})`);
        error.httpRejected = true;
        throw error;
      }
      return await response.json();
    } finally { clearTimeout(timer); }
  }

  async readCart() {
    const cart = await this.requestJSON(this.cartURL());
    this.validateCart(cart);
    return cart;
  }

  validateCart(cart) {
    if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0 ||
        !cart.attributes || typeof cart.attributes !== 'object' || Array.isArray(cart.attributes)) {
      throw new Error('Cart response could not be verified');
    }
    if ((cart.item_count === 0) !== (cart.items.length === 0)) throw new Error('Inconsistent cart response');
  }

  cartFingerprint(cart) {
    return JSON.stringify(cart.items.map((item) => [item.key || item.id, item.quantity]));
  }

  writePending(change) {
    localStorage.setItem(this.cartChangeKey, JSON.stringify(change));
    if (localStorage.getItem(this.cartChangeKey) !== JSON.stringify(change)) throw new Error('Change could not be saved');
    this.pendingChange = change;
  }

  clearPending() {
    localStorage.removeItem(this.cartChangeKey);
    if (localStorage.getItem(this.cartChangeKey)) throw new Error('Change status could not be cleared');
    this.pendingChange = null;
    this.recoveryDesired = null;
  }

  setCartBusy(busy, message = '') {
    this.cartBusy = busy;
    if (busy) {
      this.clearCartWarningAnnouncement();
      this.busyControls = Array.from(this.dialog.querySelectorAll('button, input')).map((element) => [element, element.disabled]);
      this.busyControls.forEach(([element]) => { element.disabled = true; });
      this.cartStatus.textContent = message;
      this.cartStatus.focus({ preventScroll: false });
    } else {
      (this.busyControls || []).forEach(([element, disabled]) => { element.disabled = disabled; });
      this.busyControls = null;
      this.cartStatus.textContent = '';
    }
  }

  async withCartLock(callback) {
    if (this.cartBusy) return;
    // Web Locks serializes this theme's location changes across same-origin tabs.
    if (navigator.locks?.request) {
      try {
        return await navigator.locks.request('poc-location-change', { ifAvailable: true }, async (lock) => {
        if (this.cartBusy) return;
        if (!lock) {
          this.cartFeedback.textContent = 'A shopping-location change is already running in another tab. Please wait, then try again.';
          this.cartFeedback.focus();
          return;
        }
          return callback();
        });
      } catch (error) {
        console.warn('Location change could not acquire the browser lock.', error);
        this.cartFeedback.textContent = 'We could not start the location check. Please close other shopping tabs and try again.';
        this.cartFeedback.focus();
        return;
      }
    }
    return callback();
  }

  performLocationChange(confirmed = false) {
    return this.withCartLock(() => this.executeLocationChange(confirmed));
  }

  initializeCartWarningFeedback() {
    // Mount an empty alert before the selector opens. A div avoids the theme's
    // empty-paragraph hiding rule; the existing class keeps it visually hidden.
    this.cartWarningAnnouncement = this.querySelector('#location-cart-warning-announcement');
    if (!this.cartWarningAnnouncement) {
      this.cartWarningAnnouncement = document.createElement('div');
      this.cartWarningAnnouncement.id = 'location-cart-warning-announcement';
      this.cartWarningAnnouncement.className = 'visually-hidden';
      this.cartWarningAnnouncement.setAttribute('role', 'alert');
      this.cartWarningAnnouncement.setAttribute('aria-atomic', 'true');
      this.dialog.querySelector('.popup-content').appendChild(this.cartWarningAnnouncement);
    }
    this.clearCartWarningAnnouncement();
  }

  clearCartWarningAnnouncement() {
    clearTimeout(this.cartWarningAnnouncementTimer);
    this.cartWarningAnnouncementTimer = null;
    if (this.cartWarningAnnouncement) this.cartWarningAnnouncement.textContent = '';
  }

  presentCartWarning() {
    this.clearCartWarningAnnouncement();
    // Retain the existing non-destructive initial focus, after controls re-enable.
    this.backButton.focus();
    const title = this.querySelector('#location-warning-title');
    const description = this.querySelector('#location-warning-description');
    if (!title || !description) return;
    const message = [title.textContent, description.textContent]
      .map((text) => text.replace(/\s+/g, ' ').trim()).filter(Boolean).join('. ');
    if (!message) return;
    const candidate = this.confirmedCandidate;
    const cartFingerprint = this.confirmationCart;
    // A new notification per warning presentation, after the focus transition.
    // Browser/assistive-technology speech delivery is not observable from JS.
    this.cartWarningAnnouncementTimer = setTimeout(() => {
      this.cartWarningAnnouncementTimer = null;
      if (!this.isConnected || !this.popupOpen || !this.dialog.open || this.cartBusy ||
          this.confirmInfo.classList.contains('hidden') ||
          this.confirmedCandidate !== candidate || this.confirmationCart !== cartFingerprint) return;
      const policy = this.ownerDocument.permissionsPolicy || this.ownerDocument.featurePolicy;
      if (typeof this.dialog.ariaNotify === 'function') {
        try {
          if (!policy || typeof policy.allowsFeature !== 'function' ||
              policy.allowsFeature('aria-notify')) {
            this.dialog.ariaNotify(message, { priority: 'high' });
            return; // Do not also populate the fallback alert for this warning.
          }
        } catch (error) {
          // Unsupported or denied direct notifications use the mounted alert.
        }
      }
      this.cartWarningAnnouncement.textContent = message;
    }, 150);
  }

  showCartWarning(desired, cart) {
    // The ZIP form is being hidden; do not dispatch its pending feedback later.
    this.cancelZipFeedback();
    this.locationStatus.textContent = '';
    this.confirmedCandidate = JSON.stringify(desired);
    this.confirmationCart = this.cartFingerprint(cart);
    this.backButton.textContent = this.readPendingChange() ? 'Back to Status Check' : 'Keep Current Location';
    this.recoveryInfo.classList.add('hidden');
    this.popupInfo.classList.add('hidden');
    this.confirmInfo.classList.remove('hidden');
    this.dialog.setAttribute('aria-labelledby', 'location-warning-title');
    this.dialog.setAttribute('aria-describedby', 'location-warning-description');
  }

  async executeLocationChange(confirmed) {
    if (this.cartBusy) return;
    const existing = this.readPendingChange();
    this.pendingChange = existing;
    if (existing && !this.recoveryDesired) {
      this.pendingChange = existing;
      this.showCartRecovery();
      this.querySelector('#location-recovery-title').focus();
      return;
    }
    const desired = this.recoveryDesired || this.readSelection();
    if (!desired) return;
    this.cartFeedback.textContent = '';
    let warning = false;
    let finished = false;
    this.setCartBusy(true, 'Checking your cart. Please wait.');
    try {
      const cart = await this.readCart();
      // A recovered request must never operate on a different browser cart.
      if (existing?.cartId && cart.token && existing.cartId !== cart.token.split('?')[0]) {
        throw new Error('The cart changed during location recovery');
      }
      if (this.attributesMatch(cart, this.locationAttributes(desired)) && (!existing || cart.item_count === 0)) {
        await this.commitPreferred(desired, cart);
        finished = true;
      } else if (cart.item_count > 0 && (!confirmed || this.confirmedCandidate !== JSON.stringify(desired) ||
                 this.confirmationCart !== this.cartFingerprint(cart))) {
        this.showCartWarning(desired, cart);
        warning = true;
      } else {
        // Detect unavailable browser storage BEFORE any destructive server request.
        const probeKey = `${this.cartChangeKey}:storage-test`;
        localStorage.setItem(probeKey, '1');
        if (localStorage.getItem(probeKey) !== '1') throw new Error('Browser storage unavailable');
        localStorage.removeItem(probeKey);
        const change = {
          version: 1, desired, previous: existing?.previous || this.readPreferred(),
          cartId: cart.token ? cart.token.split('?')[0] : null,
          cleared: !!existing?.cleared,
          hadItems: cart.item_count > 0 || !!existing?.hadItems,
          stage: cart.item_count ? 'clear' : 'update',
          startedAt: Date.now()
        };
        this.writePending(change);
        this.cartStatus.textContent = 'Updating your shopping location. Please wait.';
        if (cart.item_count > 0) {
          const cleared = await this.requestJSON(this.cartURL('clear'), { method: 'POST' });
          this.validateCart(cleared);
          if (cleared.item_count !== 0) throw new Error('Cart was not empty after clear');
          change.cleared = true;
          change.stage = 'update';
          this.writePending(change);
          this.synchronizeCartDisplay(cleared);
        }
        const updated = await this.requestJSON(this.cartURL('update'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: this.locationAttributes(desired) })
        });
        this.validateCart(updated);
        if (updated.item_count !== 0 || !this.attributesMatch(updated, this.locationAttributes(desired))) {
          throw new Error('Updated cart does not match the selected location');
        }
        change.stage = 'verify';
        this.writePending(change);
        const verified = await this.readCart();
        if (verified.item_count !== 0 || !this.attributesMatch(verified, this.locationAttributes(desired))) {
          throw new Error('Cart changed before location could be verified');
        }
        await this.commitPreferred(desired, verified);
        finished = true;
      }
    } catch (error) {
      console.warn('Shopping location change needs attention.', error);
      if (this.pendingChange) {
        // A timeout/connection loss is not a rejection acknowledgment. Keep the
        // recovery journal while a late server mutation may still be in flight.
        if (['clear', 'update'].includes(this.pendingChange.stage) && !error.httpRejected) {
          this.pendingChange.uncertain = true;
          try { this.writePending(this.pendingChange); } catch (storageError) {
            console.warn('Could not persist uncertain request state.', storageError);
          }
        }
        finished = await this.reconcilePendingChange();
      } else {
        this.resetPopupView();
        this.cartFeedback.textContent = 'We could not confirm your shopping location. No cart-change request was sent. Please check your connection and browser storage settings, then try again.';
      }
    } finally {
      this.setCartBusy(false);
      if (finished) this.closePopup();
      else if (warning) this.presentCartWarning();
      else this.cartFeedback.focus();
    }
  }

  async commitPreferred(desired, cart) {
    // Only publish the preference after Shopify's actual cart has been checked.
    if (!this.attributesMatch(cart, this.locationAttributes(desired))) throw new Error('Cart location mismatch');
    localStorage.setItem('preferredLocation', JSON.stringify(desired));
    if (localStorage.getItem('preferredLocation') !== JSON.stringify(desired)) throw new Error('Browser location was not saved');
    this.filterProductsByTag(desired.id);
    this.updateHeaderDisplay(desired.location, desired.method);
    this.updateHeaderDisplay(desired.location, desired.method, true);
    this.synchronizeCartDisplay(cart);
    await this.refreshCurrentVariantStock();
    this.clearPending();
  }

  async reconcilePendingChange() {
    const change = this.pendingChange || this.readPendingChange();
    if (!change?.desired) {
      this.showCartRecovery('The saved location-change status could not be read. Please contact the store for help before checking out.');
      return false;
    }
    try {
      const cart = await this.readCart();
      if (change.cartId && cart.token && change.cartId !== cart.token.split('?')[0]) throw new Error('Different cart');
      this.synchronizeCartDisplay(cart);
      // A dropped response is not proof that the server rejected the request.
      if (cart.item_count === 0 && this.attributesMatch(cart, this.locationAttributes(change.desired))) {
        await this.commitPreferred(change.desired, cart);
        return true;
      }
      const previousAttributes = this.locationAttributes(change.previous);
      if (this.attributesMatch(cart, previousAttributes)) {
        if (change.uncertain && (change.stage === 'update' || cart.item_count > 0)) {
          this.showCartRecovery('The request timed out or its response was lost. The server may still be processing it. Please wait and check the status again before retrying. Your cart cannot be used for a new purchase until the location is verified.');
          return false;
        }
        // No new preference is saved on this path; cleared products are not recreated.
        if (change.previous) {
          localStorage.setItem('preferredLocation', JSON.stringify(change.previous));
        } else localStorage.removeItem('preferredLocation');
        this.clearPending();
        this.resetPopupView();
        const previousText = change.previous ? 'Your previous shopping location is still selected.' : 'No new shopping location has been saved.';
        this.cartFeedback.textContent = (cart.item_count === 0 && (change.cleared || change.hadItems) ? 'Your cart is now empty, but the new location was not saved. ' : 'The location change did not finish. ') + previousText + ' Please review your cart and try again.';
        if (change.previous) {
          this.updateHeaderDisplay(change.previous.location, change.previous.method);
          this.updateHeaderDisplay(change.previous.location, change.previous.method, true);
        }
        return false;
      }
    } catch (error) {
      console.warn('Could not verify location change.', error);
    }
    this.showCartRecovery();
    return false;
  }

  showCartRecovery(message = '') {
    this.clearCartWarningAnnouncement();
    this.popupInfo.classList.add('hidden');
    this.confirmInfo.classList.add('hidden');
    this.recoveryInfo.classList.remove('hidden');
    this.dialog.setAttribute('aria-labelledby', 'location-recovery-title');
    this.dialog.setAttribute('aria-describedby', 'location-recovery-description');
    this.cartFeedback.textContent = message || 'We could not verify that your cart and shopping location match. Check the status again, or retry the location change. Any cart clearing will require your confirmation again. Products already cleared cannot be restored automatically.';
    this.updateHeaderDisplay('Check shopping location', 'Open to resolve');
    this.updateHeaderDisplay('Check shopping location', 'Open to resolve', true);
  }

  checkPendingChange() {
    return this.withCartLock(async () => {
      this.pendingChange = this.readPendingChange();
      if (!this.pendingChange) { this.resetPopupView(); this.cartFeedback.textContent = ''; return; }
      this.setCartBusy(true, 'Checking your cart and shopping location. Please wait.');
      let finished = false;
      try { finished = await this.reconcilePendingChange(); }
      finally {
        this.setCartBusy(false);
        if (finished) this.closePopup();
        else this.cartFeedback.focus();
      }
    });
  }

  retryPendingChange() {
    if (this.cartBusy) return;
    this.pendingChange = this.readPendingChange();
    const desired = this.pendingChange?.desired;
    if (!desired) {
      this.showCartRecovery('The location-change status could not be read. Please contact the store for help before checking out.');
      this.cartFeedback.focus();
      return;
    }
    this.recoveryDesired = desired;
    this.confirmedCandidate = null;
    this.confirmationCart = null;
    return this.performLocationChange(false);
  }

  synchronizeCartDisplay(cart) {
    if (cart.item_count === 0) {
      document.querySelectorAll('cart-items .cart-item').forEach((item) => {
        this.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true, detail: { variantId: item.dataset.variantId, newQuantity: 0 }
        }));
      });
    }
    document.querySelectorAll('cart-items').forEach((items) => this.refreshCartDrawer(items));
  }

  guardPendingCommerce(event) {
    if (!this.isConnected || this.contains(event.target) || !this.readPendingChange()) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    let commerce = target.closest('[name="add"], [name="checkout"], .js-empty-cart, .shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, cart-items button, cart-items input');
    const form = target.closest('form');
    const link = target.closest('a[href]');
    const action = form ? new URL(form.action, location.href).pathname : '';
    const href = link ? new URL(link.href, location.href).pathname : '';
    if (event.type === 'submit' && /\/(cart(?:\/add)?|checkout)(?:\.js)?\/?$/.test(action)) commerce = true;
    if (/\/(checkout|cart\/(change|clear|add))(?:\.js)?(?:\/|$)/.test(href)) commerce = true;
    if (!commerce) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.openPopup(target);
  }

  // refresh cart drawer if it has items
  async refreshCartDrawer(items) {
    try {
      await items.refreshCartItems();
    } catch (error) {
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: {
          error: error ? error.message : null
        }
      }));
    }
  }
}

if (!customElements.get('state-popup')) {
  customElements.define('state-popup', StatePopup);
}